import { Button } from '@liza/ui/components/ui/button';
import { FieldLabel } from '@liza/ui/components/ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@liza/ui/components/ui/select';
import { Textarea } from '@liza/ui/components/ui/textarea';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { LabOrderPriority, SelectedTest, TestAoe } from './AddLabOrderForm';
import { LabTestAoeFields } from './LabTestAoeFields';

// Ordered priority options for the dropdown. `satisfies` ties it to LabOrderPriority so it can't
// drift from the accepted `ServiceRequest.priority` codes.
const PRIORITY_VALUES = ['routine', 'urgent', 'asap', 'stat'] as const satisfies readonly LabOrderPriority[];

export const SelectedTestRow = ({
  test,
  name,
  onChange,
  onRemove,
  aoe,
  onAoeChange,
  showAoeErrors,
}: {
  test: SelectedTest;
  /** Display label for the test (`test.display ?? test.code`), computed by the parent. */
  name: string;
  onChange: (next: SelectedTest) => void;
  onRemove: () => void;
  /** This test's captured AOE state, or undefined before its questionnaire is fetched. */
  aoe: TestAoe | undefined;
  onAoeChange: (entry: TestAoe) => void;
  /** Flag unanswered required AOE questions (true once the form has been submitted). */
  showAoeErrors: boolean;
}): React.ReactElement => {
  // Use the `records` namespace directly (rather than receiving `t` as a prop) so the i18n
  // extractor can attribute these keys to the right namespace.
  const { t } = useTranslation('records');
  const priorityFieldId = `lab-order-test-${test.code}-priority`;
  const notesFieldId = `lab-order-test-${test.code}-notes`;

  return (
    <div className="space-y-2 rounded-md border p-2" data-testid={`selected-test-${test.code}`}>
      <div className="flex items-center gap-2">
        <span className="flex-1 text-sm">{name}</span>
        <Button
          type="button"
          size="icon-xs"
          variant="ghost"
          onClick={onRemove}
          aria-label={t('removeTest', { name })}
          className="size-5 rounded-full"
        >
          <XIcon className="size-3.5" />
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <FieldLabel htmlFor={priorityFieldId} className="w-20 shrink-0">
          {t('testPriority')}
        </FieldLabel>
        <Select
          value={test.priority ?? 'routine'}
          onValueChange={(value) => onChange({ ...test, priority: value as LabOrderPriority })}
        >
          <SelectTrigger id={priorityFieldId} className="flex-1" aria-label={t('testPriority')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRIORITY_VALUES.map((priority) => (
              <SelectItem key={priority} value={priority}>
                {t(`priority_${priority}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-start gap-2">
        <FieldLabel htmlFor={notesFieldId} className="w-20 shrink-0 pt-2">
          {t('testNotes')}
        </FieldLabel>
        <Textarea
          id={notesFieldId}
          value={test.notes ?? ''}
          onChange={(e) => onChange({ ...test, notes: e.target.value })}
          aria-label={t('testNotesFor', { name })}
          rows={2}
          className="flex-1"
        />
      </div>
      <LabTestAoeFields test={test} value={aoe} onChange={onAoeChange} showErrors={showAoeErrors} />
    </div>
  );
};
